import React from 'react'
import { ArrowLeftRight, CalendarCheck, ClipboardCheck, ClipboardPlus, CreditCard, Send, Users, Wrench } from 'lucide-react'

const LANE_WIDTH = 160
const DIAGRAM_WIDTH = LANE_WIDTH * 8

// 沿用原时序图的八个角色及字段含义，结算方式仍读取 settlementPartyName。
export function repairParticipants(info) {
    return [
        { role: '创建方式', name: info.orderSource, icon: ClipboardPlus, tone: 'blue' },
        { role: '创建人', name: info.createUserName, icon: ClipboardPlus, tone: 'blue' },
        { role: '派单人', name: info.dispatchUserName, icon: Send, tone: 'blue' },
        { role: '维修团队', name: info.teamName, icon: Users, tone: 'orange' },
        { role: '主修人', name: info.majorUserName, icon: Wrench, tone: 'orange' },
        { role: '审核人', name: info.reviewerName, icon: ClipboardCheck, tone: 'indigo' },
        { role: '结算方式', name: info.settlementPartyName, icon: CreditCard, tone: 'green' },
        { role: '工单完成', name: '工单完成', icon: CalendarCheck, tone: 'green' },
    ]
}

// 保留原图十条消息的方向、顺序及三个自调用，不把“提交”合并进下一阶段。
export function repairMessages(info) {
    return [
        { from: 0, to: 1, label: '创建工单', time: info.createTime, timed: true, tone: 'blue' },
        { from: 1, to: 2, label: '提交工单', tone: 'blue' },
        { from: 2, to: 3, label: '分配工单', time: info.dispatchTime, timed: true, tone: 'blue' },
        { from: 3, to: 4, label: `分派至${info.teamUsers || '未记录人员'}`, time: info.dispatchTime, timed: true, tone: 'blue' },
        { from: 4, to: 4, label: '开始维修', time: info.teamStartMaintenanceTime, timed: true, tone: 'orange' },
        { from: 4, to: 4, label: '完成维修', time: info.teamMaintenanceCompleteTime, timed: true, tone: 'orange' },
        { from: 4, to: 5, label: '提交审核', tone: 'indigo', openArrow: true },
        { from: 5, to: 5, label: '审核', time: info.reviewerTime, timed: true, tone: 'indigo' },
        { from: 5, to: 6, label: '结算', time: info.settlementTime, timed: true, tone: 'green' },
        { from: 6, to: 7, label: '完成时间', time: info.orderCompletedTime, timed: true, tone: 'green' },
    ]
}

function wrapLabel(value) {
    const lines = []
    let line = ''
    let width = 0
    // 固定泳道保持字号可读；长人员名单换行并增加该消息高度，不截断姓名。
    Array.from(value).forEach((character) => {
        const size = character.codePointAt(0) < 128 ? 7.5 : 13
        if (line && (width + size > 140 || character === '\n')) {
            lines.push(line)
            line = ''
            width = 0
        }
        if (character !== '\n') {
            line += character
            width += size
        }
    })
    if (line) lines.push(line)
    return lines
}

function ActorRow({ participants, footer = false }) {
    return (
        <div className={`repair-sequence-actors${footer ? ' repair-sequence-actors--footer' : ''}`} aria-hidden={footer || undefined}>
            {participants.map(({ role, name, icon: Icon, tone }) => (
                <div className="repair-sequence-actor" data-tone={tone} key={role}>
                    <div>
                        {!footer && <Icon size={16} aria-hidden="true" />}
                        <span>{role}</span>
                    </div>
                    <strong>{name || '未记录'}</strong>
                </div>
            ))}
        </div>
    )
}

export default function RepairReportFlow({ orderInfo }) {
    if (!orderInfo)
        return (
            <div className="inventory-state">
                <ClipboardCheck size={32} strokeWidth={1.4} />
                <h3>该报告未保存工单流程信息</h3>
                <p>报告正文和思维导图仍可正常查看。</p>
            </div>
        )
    const participants = repairParticipants(orderInfo)
    let height = 60
    const messages = repairMessages(orderInfo).map((message, index) => {
        const lines = wrapLabel(message.label)
        const top = height
        const arrowY = top + lines.length * 20 + (message.timed ? 20 : 0) + 8
        const self = message.from === message.to
        height = arrowY + (self ? 30 : 0) + 34
        return { ...message, index, lines, top, arrowY, self }
    })
    return (
        <section className="repair-flow" aria-label="工单流程时序图">
            <header>
                <div>
                    <span>工单流程</span>
                    <h3>角色协作与流转</h3>
                </div>
                <p>
                    <ArrowLeftRight size={15} />
                    横向滚动查看全部角色
                </p>
            </header>
            <div className="repair-sequence-scroll" tabIndex={0} aria-label="可滚动的工单流程图">
                <div className="repair-sequence">
                    <ActorRow participants={participants} />
                    <svg viewBox={`0 0 ${DIAGRAM_WIDTH} ${height}`} className="repair-sequence-chart" aria-label="按时间向下排列的工单流转消息">
                        {participants.map(({ role }, index) => (
                            <g key={role} className="repair-sequence-lane">
                                <rect x={index * LANE_WIDTH} width={LANE_WIDTH} height={height} fill={index % 2 ? '#ffffff' : '#f8faff'} />
                                <line x1={index * LANE_WIDTH + 80} x2={index * LANE_WIDTH + 80} y1={0} y2={height} />
                            </g>
                        ))}
                        <g className="repair-sequence-note">
                            <rect x={94} y={14} width={122} height={28} rx={5} />
                            <text x={155} y={33} textAnchor="middle">
                                创建维修工单
                            </text>
                        </g>
                        {messages.map(({ from, to, lines, time, timed, tone, openArrow, top, arrowY, self, index }) => {
                            const start = from * LANE_WIDTH + 80
                            const end = to * LANE_WIDTH + 80
                            const labelX = self ? start + 80 : (start + end) / 2
                            const endY = arrowY + (self ? 30 : 0)
                            const arrow = self
                                ? `${end + 7},${endY - 4} ${end},${endY} ${end + 7},${endY + 4}`
                                : `${end - 7},${endY - 4} ${end},${endY} ${end - 7},${endY + 4}`
                            return (
                                <g className="repair-sequence-message" data-tone={tone} data-self={self} key={index}>
                                    <text className="repair-sequence-label" textAnchor="middle">
                                        {lines.map((line, lineIndex) => (
                                            <tspan key={lineIndex} x={labelX} y={top + 16 + lineIndex * 20}>
                                                {line}
                                            </tspan>
                                        ))}
                                    </text>
                                    {timed && (
                                        <text className="repair-sequence-time" x={labelX} y={arrowY - 10} textAnchor="middle">
                                            {time ? time.replace('T', ' ') : '时间未记录'}
                                        </text>
                                    )}
                                    <path
                                        className="repair-sequence-arrow"
                                        d={
                                            self
                                                ? `M ${start + 12} ${arrowY} H ${start + 126} Q ${start + 132} ${arrowY} ${start + 132} ${arrowY + 6} V ${
                                                      endY - 6
                                                  } Q ${start + 132} ${endY} ${start + 126} ${endY} H ${end}`
                                                : `M ${start + 12} ${arrowY} H ${end}`
                                        }
                                    />
                                    <polyline className={`repair-sequence-arrowhead${openArrow ? ' repair-sequence-arrowhead--open' : ''}`} points={arrow} />
                                    <circle className="repair-sequence-number" cx={start} cy={arrowY} r={12} />
                                    <text className="repair-sequence-number-label" x={start} y={arrowY + 4} textAnchor="middle">
                                        {index + 1}
                                    </text>
                                </g>
                            )
                        })}
                    </svg>
                    <ActorRow participants={participants} footer />
                </div>
            </div>
        </section>
    )
}
